import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';

import { TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngxs/store';

import { SearchItem } from '@smarthead-api/models/search-item';
import { SearchResponse } from '@smarthead-api/models/search-response';
import { SearchService } from '@smarthead-api/services/search.service';

import { RouterLinkCreatorService } from '../../../../shared/services/router-link-creator.service';
import { AppState } from '../../../../shared/state/app.state';
import { ActivityUtils } from '../../../../shared/utils/activity-utils';
import { BasicEntityEnum, BasicEntityUtils } from '../../../../shared/utils/basic-entity-utils';
import { SearchItemExtended, SearchUtils } from '../../../../shared/utils/search-utils';

@Component({
  selector: 'sh-search-input',
  templateUrl: './search-input.component.html',
  styleUrl: './search-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SearchInputComponent implements OnInit {
  // TODO most probably can be removed
  // @Output() selectedSearch = new EventEmitter();

  @Output() closeSearch = new EventEmitter();

  @ViewChild('inputRef') inputRef: ElementRef | undefined;

  countries$ = this.store.select(AppState.getCountries);

  activityUtils = ActivityUtils;
  basicEntityUtils = BasicEntityUtils;
  basicEntityEnum = BasicEntityEnum;

  headerSearchForm: UntypedFormGroup = new UntypedFormGroup({});
  get headerSearch(): any {
    return this.headerSearchForm.get('headerSearch');
  }
  searchItems: SearchItemExtended[] = [];
  hashtagSearch: string | null = null;
  searchString = '';
  searchPerformed = false;

  constructor(
    private store: Store,
    private fb: UntypedFormBuilder,
    private searchService: SearchService,
    public translocoService: TranslocoService,
    public routerLinkCreatorService: RouterLinkCreatorService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    // forms inits
    this.headerSearchForm = this.fb.group({
      headerSearch: [
        null,
        [Validators.minLength(3)]
      ]
    });

    // user type something in search
    const headerSearch = this.headerSearchForm.get('headerSearch');
    if (headerSearch) {
      headerSearch.valueChanges
        .pipe(
          debounceTime(400),
          distinctUntilChanged(),
          filter(entity => entity && typeof entity === 'string'),
          switchMap(entity => {
            this.hashtagSearch = null;
            this.searchString = entity;
            if (entity && entity.startsWith('#')) {
              if (entity.length > 1) {
                this.hashtagSearch = entity.substring(1);
                if (this.hashtagSearch) {
                  return this.searchService
                    .searchByTag({
                      search: this.hashtagSearch,
                      offset: 0,
                      limit: 5
                    })
                    .pipe(
                      catchError(err => {
                        console.error(err);
                        return of({ count: 0, items: [] } as SearchResponse);
                      })
                    );
                } else {
                  return of({ count: 0, items: [] } as SearchResponse);
                }
              } else {
                return of({ count: 0, items: [] } as SearchResponse);
              }
            } else {
              return this.searchService
                .searchV2({
                  search: entity,
                  offset: 0,
                  limit: 5
                })
                .pipe(
                  catchError(err => {
                    console.error(err);
                    return of({ count: 0, items: [] } as SearchResponse);
                  })
                );
            }
          }),
          map((searchItems: SearchResponse) => searchItems.items as SearchItemExtended[])
        )
        .subscribe(
          (res: SearchItemExtended[]) => {
            if (res) {
              this.searchPerformed = true;
              SearchUtils.processSearchItems(res);
            }
            this.searchItems = res;
            this.changeDetectorRef.detectChanges();
          },
          err => {
            console.error(err);
          }
        );
    }
  }

  /**
   * SEARCH
   * Founded items can have multiple hashtags.
   * Filter only those hashtags witch contains searched string (relevant hashtags).
   */
  highlightHashtag(hashtags: string[]): string[] {
    return hashtags.filter(hashtag => hashtag.indexOf(this.hashtagSearch ? this.hashtagSearch : '') > -1);
  }

  /**
   * SEARCH
   * Used by autocomplete when user pick something from dropdown + redirect + clear parameters
   */
  displayEntityFn(picked?: SearchItem): string {
    if (picked) {
      if (picked.id.indexOf('profile') > -1) {
        this.routerLinkCreatorService.navigateToProfile(parseInt(picked.id.replace('profile-', ''), 10), picked.text);
        // this.selectedSearch.emit(picked);
        this.closeSearch.emit();
      }
      if (picked.id.indexOf('activity') > -1) {
        this.routerLinkCreatorService.navigateToActivity(parseInt(picked.id.replace('activity-', ''), 10), picked.activityTitle as any);
        // this.selectedSearch.emit(picked);
        this.closeSearch.emit();
      }
      this.headerSearchForm.reset();
      setTimeout(() => {
        if (this.inputRef) {
          this.inputRef.nativeElement.blur();
        }
      }, 10);
    }
    return picked ? picked.text : '';
  }

  /**
   * Enter pressed or selected Display all -> redirect to search page
   */
  navigateToSearch(): void {
    if (this.searchItems?.length > 0) {
      this.routerLinkCreatorService.navigateToSearch(this.hashtagSearch ? 'h_' + this.hashtagSearch : this.searchString);
      this.headerSearchForm.reset();
      // this.selectedSearch.emit({});
      this.closeSearch.emit();
      setTimeout(() => {
        if (this.inputRef) {
          this.inputRef.nativeElement.blur();
        }
      }, 10);
    }
  }
}
